/**
 * Mobile primary-navigation overlay.
 *
 * Owns the open / closed state of the full-screen menu panel that replaces the
 * header nav below xl. Opening it locks scrolling on the page behind: the panel
 * is `fixed inset-0`, so without the lock a touch drag over the overlay chains
 * through to the document and scrolls the page underneath it.
 *
 * The lock goes through the shared `scroll-lock` helper rather than setting
 * `body.style.overflow` here, because the AI assistant can be launched from a
 * menu item inside this panel and both would otherwise fight over the same
 * property — see the notes in scroll-lock.js.
 *
 * The panel itself scrolls internally ( see `.main-navigation__overlay` in
 * site-header.css ) — x-collapse pins its height to the viewport when it
 * expands, so a menu taller than the screen would otherwise clip its lower
 * items out of reach.
 *
 * Escape closes the panel, and a menu left open when the viewport crosses up
 * into desktop range closes itself so the lock never strands the page
 * unscrollable behind a hidden overlay.
 *
 * Usage:
 *   <nav x-data="mobileNav" @keydown.escape.window="close()">
 *     <button @click="openNav()">…</button>
 *     <div class="main-navigation__overlay" x-show="open" x-collapse…>
 *       <button @click="close()">…</button>
 *     </div>
 *   </nav>
 */

import { lockScroll, unlockScroll } from './scroll-lock.js';

document.addEventListener( 'alpine:init', () => {

	Alpine.data( 'mobileNav', () => ({

		open: false,

		init() {

			// The overlay only exists below xl; once the desktop nav takes over
			// an open panel is hidden by CSS, so drop the state and the lock
			// with it rather than leaving the page unscrollable.
			this.desktop = window.matchMedia( '(min-width: 80rem)' );

			this.onChange = ( event ) => {
				if ( event.matches ) {
					this.close();
				}
			};

			this.desktop.addEventListener( 'change', this.onChange );
		},

		// Alpine calls this when the x-data tree is removed. Releasing the lock
		// here matters more than the listener: a component torn down while the
		// menu is open would otherwise hold its claim forever, leaving the page
		// permanently unscrollable with no overlay left to close.
		destroy() {
			this.desktop.removeEventListener( 'change', this.onChange );
			unlockScroll( this );
		},

		openNav() {
			this.open = true;
			lockScroll( this );
		},

		close() {
			if ( ! this.open ) {
				return;
			}

			this.open = false;
			unlockScroll( this );
		},

	}) );
});
